import React, { useState, useMemo, memo, useEffect, useRef, useLayoutEffect } from 'react';
import useAPI from 'hooks/api-hooks';
import { useQuery } from 'react-query';
import { Page, pdfjs, Document } from 'react-pdf';
import { LoaderSkeleton } from 'components/layout/KitRegistrationDashboard';
import useResizeObserver from '@react-hook/resize-observer';
import { Button, MenuItem, TextField, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'AuthCtx';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => {
  return { mobileOnly: {
    display: 'none',
    margin: '20px',
    [theme.breakpoints.down('sm')]: {
      display: 'inline-flex', // or 'block', depending on your layout
    },
  } };
});

// eslint-disable-next-line import/no-extraneous-dependencies
pdfjs.GlobalWorkerOptions.workerSrc = require('pdfjs-dist/build/pdf.worker.entry');

const useWidth = (target) => {
  const [ width, setWidth ] = useState(null);

  useLayoutEffect(() => {
    setWidth(target.current.getBoundingClientRect().width);
  }, [ target ]);

  useResizeObserver(target, (entry) => { return setWidth(entry.contentRect.width); });
  return width;
};

const PDFViewerHelper = memo(({ resp, setNumPages, currentPage }) => {
  const onDocumentLoadSuccess = ({ numPages: newPages }) => {
    setNumPages(newPages);
  };

  const wrapperDiv = useRef(null);
  const width = useWidth(wrapperDiv);

  return (
    <div className='wrapper' ref={ wrapperDiv }>
      <Document
        file={ { data: resp } }
        onLoadSuccess={ onDocumentLoadSuccess }
        onLoadError={ console.error }
      >
        <Page
          pageNumber={ currentPage }
          width={ width }
        />
      </Document>
    </div>
  );
});

const PDFViewerMiddle = ({ serial, hasPDF, isDoctor: doctorReport, nameSuffix }) => {
  const { client } = useAPI();
  const history = useHistory();
  const classes = useStyles();
  const [ numPages, setNumPages ] = useState(null);
  const [ currentPage, setCurrentPage ] = useState(1);
  const { t } = useTranslation();
  const menuListRef = useRef(null);

  const { isDoctor } = useAuth();

  const {
    data,
    isLoading,
  } = useQuery(
    [ 'pdf-viewer', serial, hasPDF, doctorReport, nameSuffix ], () => {
      if (!hasPDF) {
        return null;
      }

      let name = serial;
      if (doctorReport) {
        name += '_doctor';
      } else if ('' !== nameSuffix) {
        name += nameSuffix;
      }

      return client.get(`/readPDFFile?fileName=${ name }.pdf`);
    },
  );

  if (!hasPDF) {
    return (
      <h1>Μη διαθέσιμο pdf</h1>
    );
  }

  if (isLoading) {
    return <LoaderSkeleton />;
  }

  const saveByteArray = () => {
    const u8 = new Uint8Array(data.Body.data);
    const blob = new Blob([ u8 ], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    const fileName = `${ serial }.pdf`;
    link.download = fileName;
    link.click();
  };

  const handlePrev = () => { return setCurrentPage((p) => { return p - 1; }); };
  const handleNext = () => { return setCurrentPage((p) => { return p + 1; }); };
  const handleChange = (e) => {
    const value = Number(e.target.value);
    if (0 < value && value <= numPages) {
      setCurrentPage(value);
    }
  };

  return (
    <div style={ { width: '100%' } }>
      <div style={ { display: 'flex',
        width: '100%',
        justifyContent: 'space-evenly',
        marginBottom: '1rem',
        alignItems: 'center' } }
      >
        <h1>
          PDF
        </h1>

        {1 < currentPage && (
          <Button onClick={ handlePrev }>
            {t('PrevFem')}
          </Button>
        )}
        {currentPage < numPages && (
          <Button onClick={ handleNext }>
            {t('NextFem')}
          </Button>
        )}

        <Typography variant='body1'>
          <TextField
            size='small'
            type='number'
            value={ currentPage }
            select
            onChange={ handleChange }
            SelectProps={ {
              MenuProps: {
                PaperProps: {
                  style: {
                    maxHeight: 300,
                    overflowY: 'auto',
                  },
                },
                MenuListProps: {
                  ref: menuListRef,
                },
                onEntered: () => {
                  if (menuListRef.current) {
                    const activeItem = menuListRef.current.querySelector(`[data-value="${ currentPage }"]`);
                    if (activeItem) {
                      activeItem.scrollIntoView({ block: 'center' });
                    }
                  }
                },
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
                getContentAnchorEl: null,
              },
            } }
          >
            {Array.from({ length: numPages }, (_, i) => {
              return (
                <MenuItem key={ i + 1 } value={ i + 1 }>
                  {i + 1}
                </MenuItem>
              );
            })}
          </TextField>
          {` ${ t('out of') } ${ numPages }`}
        </Typography>

        <Button
          onClick={ saveByteArray }
          variant='contained'
          color='secondary'
          style={ { textTransform: 'none',
            color: isDoctor ? 'white' : '',
            height: 70,
            width: 190,
            fontSize: 18 } }
        >
          {t('download1')}
          {' '}
          PDF
        </Button>
      </div>

      <PDFViewerHelper
        resp={ data.Body.data }
        setNumPages={ setNumPages }
        currentPage={ currentPage }
      />
      <Button
        className={ classes.mobileOnly }
        variant='contained'
        style={ { background: '#5a7711', color: 'white', maxWidth: '230px' } }
        onClick={ () => { history.goBack(); } }
      >
        {t('back')}
      </Button>
    </div>
  );
};

const PDFViewer = ({ serial, isDoctor = false, nameSuffix = '', fromInvitation = false }) => {
  const { client } = useAPI();
  const [ hasPDF, setHasPDF ] = useState(false);

  const {
    data: kit,
    isLoading,
  } = useQuery(
    [ 'registration-kit-one', serial, fromInvitation ],
    () => {
      if (fromInvitation) {
        return client.get(`customer-doctor-kits?registration_kit.serial_number_id.serial=${ serial }`).then((s) => {
          return [ s?.[0]?.registration_kit ];
        });
      }

      return client.get(`registration-kits?serial_number_id.serial=${ serial }`);
    },
  );

  useEffect(() => {
    if (!isLoading) {
      setHasPDF(kit[0].pdf_available);
    }
  }, [ kit ]);

  if (isLoading || serial === undefined) {
    return <LoaderSkeleton />;
  }

  return (
    <PDFViewerMiddle
      serial={ serial }
      hasPDF={ hasPDF }
      isDoctor={ isDoctor }
      nameSuffix={ nameSuffix }
    />
  );
};

export const FilePDFViewer = ({ path, showDownload = false }) => {
  const [ numPages, setNumPages ] = useState(null);

  const onDocumentLoadSuccess = ({ numPages: newPages }) => {
    setNumPages(newPages);
  };

  const { t } = useTranslation();
  const wrapperDiv = useRef(null);
  const width = useWidth(wrapperDiv);

  const PDF = useMemo(() => {
    return (
      <div className='wrapper' ref={ wrapperDiv }>
        <Document
          file={ path }
          onLoadSuccess={ onDocumentLoadSuccess }
          onLoadError={ console.error }
        >
          {
            Array.from(new Array(numPages), (el, index) => {
              return (
                <Page
                  pageNumber={ index + 1 }
                  width={ width }
                  key={ `page_${ index + 1 }` }
                />
              );
            })
          }

        </Document>
      </div>
    );
  }, [ path, onDocumentLoadSuccess, numPages, width ]);

  return (
    <div style={ { height: '77vh', overflow: 'auto', width: '100%', margin: 0 } }>
      {
        showDownload && (
          <Button
            variant='contained'
            color='secondary'
          >
            <a
              href={ path }
              download
              target='_blank'
              rel='noreferrer'
              style={ { textDecoration: 'none', color: 'white' } }
            >
              {t('downloadFormal')}
            </a>
          </Button>
        )
      }
      <div>
        {PDF}
      </div>
    </div>
  );
};

PDFViewerHelper.displayName = 'PDFViewerHelper';
export default PDFViewer;
